import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../contexts/AuthContext';
import { StoreContext } from '../contexts/StoreContext';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { Table, Modal, Button, DropdownButton, Dropdown, Container } from 'react-bootstrap';

const STORE_API_URL = process.env.REACT_APP_API_URL;

function Messages() {
  const { token } = useContext(AuthContext);
  const { selectedStore, stores, setSelectedStore, fetchStores } = useContext(StoreContext);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const [typeFilter, setTypeFilter] = useState('');
  const [typeFilterTitle, setTypeFilterTitle] = useState('All Types');
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);

  useEffect(() => {
    if (token) {
      fetchStores();
      if (selectedStore) {
        fetchMessages(selectedStore, typeFilter, 1); // Reset to page 1 on filter change
      }
    }
  }, [token, selectedStore, typeFilter]);

  const fetchMessages = async (storeId, filter, pageNumber) => {
    setLoading(true);
    setError(null);

    try {
      const response = await fetch(
        `${STORE_API_URL}/messages/${storeId}?type=${filter}&page=${pageNumber}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!response.ok) {
        throw new Error('Error fetching messages.');
      }
      const data = await response.json();

      if (pageNumber === 1) {
        setMessages(data.messages); // Replace messages on the first page
      } else {
        setMessages((prev) => [...prev, ...data.messages]); // Append to existing messages
      }
      setHasMore(data.hasMore); // Update whether more pages are available
    } catch (error) {
      setError(error.message || 'An error occurred while fetching messages.');
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleStoreChange = (e) => {
    const newStoreId = e.target.value;
    setSelectedStore(newStoreId);
    localStorage.setItem('selectedStore', newStoreId); // Save selected store to localStorage
  };

  const handleTypeChange = (filter, title) => {
    setTypeFilter(filter);
    setTypeFilterTitle(title);
    setPage(1); // Reset to the first page
  };

  const loadMoreMessages = () => {
    const nextPage = page + 1;
    setPage(nextPage);
    fetchMessages(selectedStore, typeFilter, nextPage);
  };

  const openMessageModal = (message) => {
    setSelectedMessage(message);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setSelectedMessage(null);
    setIsModalOpen(false);
  };

  return (
    <Container>
      <ToastContainer />

      <div className="headline d-flex justify-content-between align-items-center">
        <h2 className="underline">Messages</h2>
        {selectedStore && stores.length > 0 && (
          <>
            {/* Store Filter */}
            <div className="form-group">
              <label></label>
              <select
                id="storeSelect"
                className="form-select"
                value={selectedStore || ''}
                onChange={handleStoreChange} // Use the handleStoreChange function
              >
                {stores.map((store) => (
                  <option key={store._id} value={store._id}>
                    {store.name}
                  </option>
                ))}
              </select>
            </div>
          </>
        )}
      </div>

      <>
        <div className="filter-buttons mb-3">
          <DropdownButton id="type-filter" title={typeFilterTitle} align="end">
            <Dropdown.Item onClick={() => handleTypeChange('', 'All Types')}>All Types</Dropdown.Item>
            <Dropdown.Item onClick={() => handleTypeChange('waitlist_signup', 'Waitlist Signup')}>
              Waitlist Signup
            </Dropdown.Item>
            <Dropdown.Item onClick={() => handleTypeChange('notification', 'Notification')}>
              Notification
            </Dropdown.Item>
            <Dropdown.Item onClick={() => handleTypeChange('feedback', 'Feedback')}>
              Feedback
            </Dropdown.Item>
            <Dropdown.Item onClick={() => handleTypeChange('settings', 'Test & Settings')}>
              Test & Settings
            </Dropdown.Item>
          </DropdownButton>
        </div>
      </>

      {loading && <div>Loading...</div>}
      {error && <div style={{ color: 'red' }}>{error}</div>}

      {!loading && !error && messages.length === 0 && selectedStore && (
        <div>No messages available for the selected store.</div>
      )}

      {!loading && !error && messages.length > 0 && (
        <div>
          <Table striped bordered hover>
            <thead>
              <tr>
                <th>From</th>
                <th>To</th>
                <th>Subject</th>
                <th>Type</th>
                <th>Status</th>
                <th>Sent At</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {messages.map((message) => (
                <tr key={message._id}>
                  <td>{message.from}</td>
                  <td>{message.to}</td>
                  <td>{message.subject}</td>
                  <td>{message.type}</td>
                  <td>{message.status}</td>
                  <td>{new Date(message.sent_at).toLocaleString()}</td>
                  <td>
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={() => openMessageModal(message)}
                    >
                      View
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
          {hasMore && (
            <div className="text-center mt-3">
              <Button onClick={loadMoreMessages} disabled={loading}>
                {loading ? 'Loading...' : 'Load More'}
              </Button>
            </div>
          )}
        </div>
      )}

      <Modal show={isModalOpen} onHide={closeModal}>
        <Modal.Header closeButton>
          <Modal.Title>Message Details</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedMessage && (
            <div>
              <p><strong>From:</strong> {selectedMessage.from}</p>
              <p><strong>To:</strong> {selectedMessage.to}</p>
              <p><strong>Subject:</strong> {selectedMessage.subject}</p>
              <p><strong>Type:</strong> {selectedMessage.type}</p>
              <p><strong>Status:</strong> {selectedMessage.status}</p>
              <p><strong>Sent At:</strong> {new Date(selectedMessage.sent_at).toLocaleString()}</p>
              <p><strong>Body:</strong></p>
              <pre>{selectedMessage.text}</pre>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closeModal}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
}

export default Messages;
